import { Flex, Text } from '@mantine/core';
import { createNumberColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/NumberColumn.tsx';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn.tsx';
import {
	createSingleLineHeader,
	SINGLE_LINE_STYLE,
} from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/singleLine.ts';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting.ts';
import { AmountFormatted } from '@dealstack/frontend/shared/components/AmountFormatted/AmountFormatted.tsx';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types.ts';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { LoanDefinitionType } from '@dealstack/shared/lib/generated';
import { InvestorLoanRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/LoansCard/investorLoans.types.ts';

interface Props {
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>;
	getLoanTypeLabel: (type: LoanDefinitionType) => string;
	showIssuer?: boolean;
}

export const getInvestorLoansTableColumns = ({
	tMipManage,
	getLoanTypeLabel,
	showIssuer = false,
}: Props): ExtendedColumnDef<InvestorLoanRow>[] => [
	createTextColumn<InvestorLoanRow>({
		id: 'loanInformation',
		size: 240,
		minSize: 200,
		accessorFn: (row) => row.name,
		header: createSingleLineHeader<InvestorLoanRow>(
			tMipManage('investors.details.loans_table.columns.loan_information')
		),
		columnLabel: tMipManage('investors.details.loans_table.columns.loan_information'),
		cell: ({ row }) => (
			<Flex direction="column" miw={0}>
				<Text fz={14} c="dark.9" truncate style={SINGLE_LINE_STYLE}>
					{row.original.name}
				</Text>

				<Text fz={12} c="dark.5" truncate style={SINGLE_LINE_STYLE}>
					{row.original.isViaVehicle
						? tMipManage('investors.details.loans_table.borrowed_via', {
								type: getLoanTypeLabel(row.original.loanType),
								issuer: row.original.issuerName,
							})
						: getLoanTypeLabel(row.original.loanType)}
				</Text>
			</Flex>
		),
	}),

	...(showIssuer
		? [
				createTextColumn<InvestorLoanRow>({
					id: 'issuer',
					size: 160,
					minSize: 140,
					accessorFn: (row) => row.issuerName,
					header: createSingleLineHeader<InvestorLoanRow>(
						tMipManage('investors.details.loans_table.columns.issuer')
					),
					columnLabel: tMipManage('investors.details.loans_table.columns.issuer'),
				}),
			]
		: []),

	createTextColumn<InvestorLoanRow>({
		id: 'provider',
		size: 160,
		minSize: 140,
		accessorFn: (row) => row.funderName,
		header: createSingleLineHeader<InvestorLoanRow>(
			tMipManage('investors.details.loans_table.columns.provider')
		),
		columnLabel: tMipManage('investors.details.loans_table.columns.provider'),
	}),

	createNumberColumn<InvestorLoanRow>({
		id: 'amount',
		size: 120,
		minSize: 120,
		accessorKey: 'amount',
		header: createSingleLineHeader<InvestorLoanRow>(
			tMipManage('investors.details.loans_table.columns.amount'),
			{ align: 'right' }
		),
		columnLabel: tMipManage('investors.details.loans_table.columns.amount'),
		cell: ({ row }) => (
			<Flex justify="flex-end" w="100%">
				<Text style={SINGLE_LINE_STYLE}>
					<AmountFormatted
						value={row.original.amount}
						currency={row.original.currency}
						decimalScale={0}
						showZero
					/>
				</Text>
			</Flex>
		),
	}),
];
